import * as React from 'react';
import { useSession, UserRole } from '@/state/session';
import { canAccessRoute } from '@/lib/rbac';

/**
 * RoleGuard seguro:
 * - TODOS os hooks no topo (ordem estável)
 * - NUNCA renderiza children durante loading de papel
 * - Sem hooks dentro de condicionais
 */
type Props = {
  children: React.ReactNode;
  require?: UserRole[]; // opcional: restringir por papéis específicos
};

export function RoleGuard({ children, require }: Props) {
  // 🔒 Hooks sempre no topo
  const { user, isLoadingRole } = useSession();

  // Log leve (opcional)
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log('🎭 RoleGuard STATE', {
        isLoadingRole,
        hasUser: !!user,
        role: user?.role,
      });
    }
  }, [isLoadingRole, user?.role]);

  // Cálculo puro de autorização
  const routeAllowed = user?.role ? canAccessRoute(user.role, window.location.pathname) : false;
  const roleAllowed = !require || (user?.role ? require.includes(user.role) : false);
  const isAuthorized = routeAllowed && roleAllowed;

  // Loading: não renderiza children
  if (isLoadingRole) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center space-y-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent mx-auto" />
          <p className="text-sm text-muted-foreground">Carregando permissões…</p>
        </div>
      </div>
    );
  }

  // Sem acesso
  if (!isAuthorized) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center space-y-3 max-w-md">
          <p className="text-lg font-semibold">Acesso negado</p>
          <p className="text-sm text-muted-foreground">
            Seu perfil não tem permissão para acessar esta área.
          </p>
          <a
            href="/dashboard"
            className="inline-flex items-center rounded-md border px-3 py-1.5 text-sm"
          >
            Voltar ao painel
          </a>
        </div>
      </div>
    );
  }

  // Autorizado
  return <>{children}</>;
}

export default RoleGuard;
